import _ from 'lodash';

export const QUOTATION_BUCKETS = [
	'best_offers',
	'ai_offers',
	'similar_offers',
] as const;

export type QuotationBucket = typeof QUOTATION_BUCKETS[number];

const COMPARED_FIELDS: { field: string; label: string; money?: boolean }[] = [
	{ field: 'prezzo_accessori_no_iva', label: 'accessori', money: true },
	{ field: 'updated_canone', label: 'canone', money: true },
	{ field: 'durata', label: 'durata' },
	{ field: 'km_annui', label: 'km annui' },
	{ field: 'anticipo', label: 'anticipo', money: true },
	{ field: 'updated_commissioni', label: 'commissioni', money: true },
	{ field: 'percentage', label: '%' },
];

/** Sotto questa soglia due importi sono considerati uguali (arrotondamenti). */
const EPSILON = 0.01;

export type QuotationFieldDiff = {
	field: string;
	label: string;
	legacy: any;
	v2: any;
	delta: number | null;
};

export type QuotationRowDiff = {
	bucket: QuotationBucket;
	rowKey: string;
	noleggiatore: string;
	kind: 'only_legacy' | 'only_v2' | 'values';
	fields: QuotationFieldDiff[];
};

export const quotationRowKey = (quotation: any, occurrence = 0) =>
	`${String(quotation?.noleggiatore ?? '').toLowerCase()}#${occurrence}`;

const indexBucket = (rows: any[] = []) => {
	const counters: Record<string, number> = {};
	const byKey = new Map<string, any>();

	rows.forEach((row) => {
		const noleggiatore = String(row?.noleggiatore ?? '').toLowerCase();
		const occurrence = counters[noleggiatore] ?? 0;
		counters[noleggiatore] = occurrence + 1;
		byKey.set(quotationRowKey(row, occurrence), row);
	});

	return byKey;
};

const isBlank = (value: any) =>
	value === null || value === undefined || value === '';

const compareField = (
	legacy: any,
	v2: any
): QuotationFieldDiff['delta'] | false => {
	if (isBlank(legacy) && isBlank(v2)) return false;

	const bothNumeric = _.isFinite(Number(legacy)) && _.isFinite(Number(v2));
	if (bothNumeric) {
		const delta = Number(v2) - Number(legacy);
		return Math.abs(delta) < EPSILON ? false : delta;
	}

	return _.isEqual(legacy, v2) ? false : null;
};

/**
 * Confronta i due set di quotazioni bucket per bucket.
 * Ritorna una riga di diff per ogni offerta presente in un solo endpoint o con valori divergenti.
 */
export const diffQuotations = (
	legacyQuotations: any,
	v2Quotations: any
): QuotationRowDiff[] => {
	if (_.isEmpty(legacyQuotations) || _.isEmpty(v2Quotations)) return [];

	const diffs: QuotationRowDiff[] = [];

	QUOTATION_BUCKETS.forEach((bucket) => {
		const legacyRows = indexBucket(legacyQuotations?.[bucket]);
		const v2Rows = indexBucket(v2Quotations?.[bucket]);
		const rowKeys = _.union([...legacyRows.keys()], [...v2Rows.keys()]);

		rowKeys.forEach((rowKey) => {
			const legacyRow = legacyRows.get(rowKey);
			const v2Row = v2Rows.get(rowKey);
			const noleggiatore = String(
				(legacyRow ?? v2Row)?.noleggiatore ?? ''
			).toUpperCase();

			if (!v2Row) {
				diffs.push({
					bucket,
					rowKey,
					noleggiatore,
					kind: 'only_legacy',
					fields: [],
				});
				return;
			}

			if (!legacyRow) {
				diffs.push({
					bucket,
					rowKey,
					noleggiatore,
					kind: 'only_v2',
					fields: [],
				});
				return;
			}

			const fields: QuotationFieldDiff[] = [];
			COMPARED_FIELDS.forEach(({ field, label }) => {
				const delta = compareField(legacyRow[field], v2Row[field]);
				if (delta === false) return;

				fields.push({
					field,
					label,
					legacy: legacyRow[field],
					v2: v2Row[field],
					delta,
				});
			});

			if (fields.length) {
				diffs.push({ bucket, rowKey, noleggiatore, kind: 'values', fields });
			}
		});
	});

	return diffs;
};

export const formatDiffValue = (value: any) =>
	isBlank(value)
		? '—'
		: _.isFinite(Number(value))
		? Intl.NumberFormat('it', { maximumFractionDigits: 2 }).format(
				Number(value)
		  )
		: String(value);
